import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from '../../shared/Modal'
import Select from '../../ui/Select/Select'
import TextInput from '../../ui/TextInput'
import Checkbox from '../../ui/Checkbox'
import Button from '../../ui/Button'
import { showToast } from '../../shared/Toast'
import useAssignableUsers from '../hooks/useAssignableUsers'
import groupUsersByRole from '../shared/groupUsersByRole'
import ModalHeader from '../shared/ModalHeader'
import AlertsService from '../../../services/api/AlertsService'
import {
  MODAL_BODY_CLASS, MODAL_FIELD_CLASS, MODAL_LABEL_CLASS,
  MODAL_FOOTER_END_CLASS, MODAL_CANCEL_BTN_CLASS
} from '../shared/styles'

const RESOURCE_ROLE_OPTIONS = [
  { value: 'operator', label: 'Opérateur' },
  { value: 'limited', label: 'Lecteur limité' },
  { value: 'reader', label: 'Lecteur' },
  { value: 'admin', label: 'Administrateur' }
]

const SUBMIT_ERROR = 'Une erreur est survenue. Veuillez réessayer.'
// Backend users_presence reports the same message on these three attributes; surface it once.
const RECIPIENT_ERROR_KEYS = ['users', 'resource_users_roles', 'last_user_performing_control']
const CHECKBOX_LABEL_CLASS = 'tw:flex! tw:items-center tw:gap-2 tw:cursor-pointer tw:m-0!'
const CHECKBOX_TEXT_CLASS = 'tw:text-sm tw:text-gray-900'

const CreateAlertModal = ({
  collectId, draftId = null, isOpen, onClose, toastContainerId = null
}) => {
  const recipientOptions = useAssignableUsers(collectId, isOpen, toastContainerId)
  const groupedRecipients = useMemo(() => groupUsersByRole(recipientOptions), [recipientOptions])
  const [name, setName] = useState('')
  const [recipients, setRecipients] = useState([])
  const [resourceRoles, setResourceRoles] = useState([])
  const [lastUser, setLastUser] = useState(false)
  const [nameError, setNameError] = useState('')
  const [recipientsError, setRecipientsError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen)
      return
    setName('')
    setRecipients([])
    setResourceRoles([])
    setLastUser(false)
    setNameError('')
    setRecipientsError('')
    setSubmitting(false)
  }, [isOpen])

  const handleNameChange = useCallback(event => {
    setName(event.target.value)
    setNameError('')
  }, [])
  const handleRecipientsChange = useCallback(value => {
    setRecipients(value || [])
    setRecipientsError('')
  }, [])
  const handleRolesChange = useCallback(value => {
    setResourceRoles(value || [])
    setRecipientsError('')
  }, [])
  const handleLastUserChange = useCallback(event => {
    setLastUser(event.target.checked)
    setRecipientsError('')
  }, [])

  const applyServerErrors = useCallback(formattedErrors => {
    const nameMessage = formattedErrors.name?.[0] || ''
    const recipientMessage = RECIPIENT_ERROR_KEYS.map(key => formattedErrors[key]?.[0]).find(Boolean) || ''
    setNameError(nameMessage)
    setRecipientsError(recipientMessage)
    if (!nameMessage && !recipientMessage)
      showToast(SUBMIT_ERROR, 'error', toastContainerId)
  }, [toastContainerId])

  const handleSubmit = useCallback(async() => {
    if (submitting)
      return
    setSubmitting(true)
    setNameError('')
    setRecipientsError('')
    try {
      const result = await AlertsService.createAlert(collectId, {
        name: name.trim(),
        userIds: recipients.map(option => option.value),
        resourceRoles: resourceRoles.map(option => option.value),
        lastUserPerformingControl: lastUser
      })
      if (result.success) {
        if (!result.id)
          throw new Error(SUBMIT_ERROR)
        const editPath = `/collects/${collectId}/alerts/${result.id}/edit`
        window.location.assign(draftId ? `${editPath}?draft_id=${draftId}` : editPath)
        return
      }
      applyServerErrors(result.formattedErrors)
      setSubmitting(false)
    } catch (caught) {
      showToast(caught?.message || SUBMIT_ERROR, 'error', toastContainerId)
      setSubmitting(false)
    }
  }, [
    submitting,
    name,
    recipients,
    resourceRoles,
    lastUser,
    collectId,
    draftId,
    toastContainerId,
    applyServerErrors
  ])

  const footer = (
    <div className={MODAL_FOOTER_END_CLASS}>
      <Button
        className={MODAL_CANCEL_BTN_CLASS}
        disabled={submitting}
        variant="neutral"
        onClick={onClose}
      >
        Annuler
      </Button>
      <Button disabled={submitting} variant="primary" onClick={handleSubmit}>
        {submitting ? 'Création...' : 'Continuer la configuration'}
      </Button>
    </div>
  )

  return (
    <Modal
      footer={footer}
      header={<ModalHeader title="Créer une alerte" onClose={onClose} />}
      isOpen={isOpen}
      title="Créer une alerte"
      onClose={onClose}
    >
      <div className={MODAL_BODY_CLASS}>
        <div className={MODAL_FIELD_CLASS}>
          <label className={MODAL_LABEL_CLASS}>
            Nom de l’alerte (usage interne) *
          </label>
          <TextInput
            errorMessage={nameError}
            name="create-alert-name"
            placeholder="Saisir"
            value={name}
            onChange={handleNameChange}
          />
        </div>
        <div className={MODAL_FIELD_CLASS}>
          <label className={MODAL_LABEL_CLASS}>
            Destinataires de l’alerte
          </label>
          <Select
            closeMenuOnSelect={false}
            error={Boolean(recipientsError)}
            menuPortalTarget={document.body}
            options={groupedRecipients}
            placeholder="Choisir"
            value={recipients}
            isMulti
            onChange={handleRecipientsChange}
          />
          {recipientsError ? <div className="text-danger small mt-1">{recipientsError}</div> : null}
        </div>
        <div className={MODAL_FIELD_CLASS}>
          <label className={MODAL_LABEL_CLASS}>
            Alerter les personnes rattachées à la ressource
          </label>
          <Select
            closeMenuOnSelect={false}
            menuPortalTarget={document.body}
            options={RESOURCE_ROLE_OPTIONS}
            placeholder="Choisir"
            value={resourceRoles}
            isMulti
            onChange={handleRolesChange}
          />
        </div>
        <label className={CHECKBOX_LABEL_CLASS}>
          <Checkbox checked={lastUser} onChange={handleLastUserChange} />
          <span className={CHECKBOX_TEXT_CLASS}>
            Alerter le dernier utilisateur ayant effectué le contrôle
          </span>
        </label>
      </div>
    </Modal>
  )
}

CreateAlertModal.propTypes = {
  collectId: PropTypes.number.isRequired,
  draftId: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  toastContainerId: PropTypes.string
}

CreateAlertModal.defaultProps = {
  draftId: null,
  toastContainerId: null
}

export default CreateAlertModal
